import AppModal from '@common/components/AppModal';
import LabeledTextField from '@common/ui/LabeledTextField';
import { TooltipButton } from '@common/ui/TooltipButton';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 124;

export enum ApprovalAction {
  Approve = 'approve',
  Reject = 'reject',
}
interface BaseActionModalProps {
  approverJustificationRequired: boolean;
  actionHandler: (data: { justification?: string }) => void;
  popoverTitle: string;
  buttonLabel: string;
  analyticsEvent: AnalyticsEvents;
  isApproverJustificationEnabled: boolean;
  displayBanner?: boolean;
}

interface ActionModalProps extends BaseActionModalProps {
  isLoading: boolean;
}

interface RequestActionModalBaseProps extends BaseActionModalProps {
  onClose: () => void;
}

export function RequestActionModal({
  approverJustificationRequired,
  actionHandler,
  isLoading,
  analyticsEvent,
  isApproverJustificationEnabled,
  popoverTitle,
  buttonLabel,
  displayBanner,
}: ActionModalProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <LoadingButton
        data-trigger={analyticsEvent}
        variant={displayBanner ? 'outlined' : 'contained'}
        size="small"
        onClick={() => setOpen(true)}
        loading={isLoading}
      >
        {buttonLabel}
      </LoadingButton>
      {open && (
        <RequestActionModalBase
          actionHandler={actionHandler}
          buttonLabel={buttonLabel}
          popoverTitle={popoverTitle}
          analyticsEvent={analyticsEvent}
          approverJustificationRequired={approverJustificationRequired}
          onClose={() => setOpen(false)}
          isApproverJustificationEnabled={isApproverJustificationEnabled}
          displayBanner={displayBanner}
        />
      )}
    </>
  );
}

function RequestActionModalBase({
  approverJustificationRequired,
  actionHandler,
  onClose,
  popoverTitle,
  buttonLabel,
  analyticsEvent,
  isApproverJustificationEnabled,
  displayBanner,
}: RequestActionModalBaseProps) {
  const { track } = useAnalyticsContext();
  const [reason, setReason] = useState('');

  const handleClose = () => {
    onClose();
    setReason('');
    track(AnalyticsEvents.ProvideReasonJustificationCancelButtonClicked);
  };

  const handleRequest = () => {
    actionHandler(isApproverJustificationEnabled ? { justification: reason } : {});
    handleClose();
  };

  const requiredFieldText = 'Please fill in the required field';
  const exceededMaximumLengthText = 'Reason exceeds maximum length';

  const validReasonLength = reason.length >= MIN_REASON_LENGTH && reason.length <= MAX_REASON_LENGTH;

  const [canSubmit, error] = useMemo(() => {
    const validInput = approverJustificationRequired ? validReasonLength : reason.length <= MAX_REASON_LENGTH;
    const canSubmit = isApproverJustificationEnabled ? validInput : true;
    const error = !canSubmit
      ? reason.length <= MIN_REASON_LENGTH
        ? requiredFieldText
        : exceededMaximumLengthText
      : '';
    return [canSubmit, error];
  }, [approverJustificationRequired, reason, validReasonLength, isApproverJustificationEnabled]);

  return (
    <AppModal open={true} onClose={handleClose} title={popoverTitle} closeButton>
      <Stack direction="column" spacing={2}>
        {isApproverJustificationEnabled && (
          <>
            {displayBanner && <RejectAlertBanner />}
            <LabeledTextField
              label="Reason"
              value={reason}
              minRows={2}
              onChange={(e) => setReason(e.target.value)}
              multiline
              optional={!approverJustificationRequired}
            />
          </>
        )}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="text" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <TooltipButton
            onClick={handleRequest}
            disabled={!canSubmit}
            label={`${buttonLabel} Request`}
            tooltip={error}
            data-trigger={analyticsEvent}
            data-props={JSON.stringify({ approverJustificationRequired: approverJustificationRequired })}
            testId="dialog-yes-button"
          />
        </Stack>
      </Stack>
    </AppModal>
  );
}

function RejectAlertBanner() {
  return (
    <Alert severity="info" variant="outlined">
      <Typography variant="caption">
        Please note that this reason will be visible to the user who made the request.
      </Typography>
    </Alert>
  );
}
